import { CSSProperties } from 'react';

import { reactSelectAdminStyles } from '../../react-select-styles';

type Params = {
  controlWidth?: CSSProperties['width'];
  menuListMaxHeight?: CSSProperties['maxHeight'];
  hasSelectedOptions: boolean;
  isFocusedWithin: boolean;
  styles?: ReturnType<typeof reactSelectAdminStyles>;
};

const getTruncatedCreatableMultiReactSelectStyles = ({
  controlWidth,
  menuListMaxHeight,
  hasSelectedOptions,
  isFocusedWithin,
  styles,
}: Params) => {
  const {
    container,
    control,
    menu,
    menuList,
    valueContainer,
    input,
    dropdownIndicator,
    indicatorsContainer,
    option,
    placeholder,
    ...restOfStyles
  } = styles || {};
  return reactSelectAdminStyles({
    container: () => ({ width: '100%', ...container }),
    control: (base, props) => ({
      padding: '0 !important',
      minHeight: '32px',
      ...(controlWidth ? { width: controlWidth, maxWidth: controlWidth } : {}),
      overflow: 'hidden',
      ...(control ? control(base, props) : {}),
    }),
    menu: (base, props) => ({
      minHeight: '36px',
      ...(menu ? menu(base, props) : {}),
    }),
    menuList: (base, props) => ({
      ...(menuListMaxHeight ? { maxHeight: menuListMaxHeight } : {}),
      ...(menuList ? menuList(base, props) : {}),
    }),
    valueContainer: (base, props) => ({
      height: '100%',
      maxHeight: '118px',
      minHeight: '32px',
      paddingTop: hasSelectedOptions || isFocusedWithin ? '4px' : '0',
      paddingBottom: hasSelectedOptions || isFocusedWithin ? '4px' : '0',
      paddingLeft: hasSelectedOptions && !isFocusedWithin ? '0' : '4px',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
      '> .truncated-creatable-multi-react-select__input-container': {
        height: '32px',
        padding: '0',
      },
      ...(valueContainer ? valueContainer(base, props) : {}),
    }),
    input: (base, props) => ({ margin: '0', height: '28px', ...(input ? input(base, props) : {}) }),
    dropdownIndicator: (base, props) => ({
      ...(hasSelectedOptions ? {} : { opacity: '1 !important' }),
      svg: {
        fill: 'var(--gray-700)',
      },
      ...(dropdownIndicator ? dropdownIndicator(base, props) : {}),
    }),
    indicatorsContainer: (base, props) => {
      return {
        ...base,
        /*
          this display none is required for the truncated viewable items calculations
          so that the indicators don't take up space and we get the full width of the available container
        */
        ...(hasSelectedOptions && !isFocusedWithin ? { display: 'none' } : {}),
        height: '32px',
        margin: 'auto',
        ...(indicatorsContainer ? indicatorsContainer(base, props) : {}),
      };
    },
    option: (base, props) => ({
      cursor: 'pointer',
      background: 'transparent',
      ...(props.isSelected
        ? {
            background: 'transparent',
          }
        : {}),
      ...(props.isFocused
        ? {
            background: 'var(--gray-800)',
          }
        : {}),
      ...(option ? option(base, props) : {}),
    }),
    placeholder: (base, props) => ({
      color: 'var(--gray-500)',
      ...(placeholder ? placeholder(base, props) : {}),
    }),
    ...restOfStyles,
  });
};

export default getTruncatedCreatableMultiReactSelectStyles;
